import { MYSQL_LIMITS } from "./constants";

export const QTY_SCALE = 4;
export const MONEY_SCALE = 2;
export const QTY_LIMITS = MYSQL_LIMITS.DECIMAL(18, QTY_SCALE);
export const MONEY_LIMITS = MYSQL_LIMITS.DECIMAL(18, MONEY_SCALE);
export const ITEM_NAME_MAX_LENGTH = 150;

const qtyFactor = 10 ** QTY_SCALE;
const moneyFactor = 10 ** MONEY_SCALE;

export const isFractionalItem = (
	item?: { IsFractional?: unknown; isFractional?: unknown } | null,
): boolean => item?.IsFractional === true || item?.isFractional === true;

/**
 * Round half away from zero, like MySQL DECIMAL and the backend (ROUND_HALF_UP).
 * Shifts through the decimal string so 1.005 rounds to 1.01, not 1.00
 * (1.005 * 100 is 100.49999... in binary floating point).
 */
const roundScaled = (value: number, scale: number): number => {
	if (!Number.isFinite(value)) return value;
	const abs = Math.abs(value);
	const text = String(abs);
	const rounded = text.includes("e")
		? Math.round(abs * 10 ** scale) / 10 ** scale
		: Number(`${Math.round(Number(`${text}e${scale}`))}e-${scale}`);
	return value < 0 && rounded !== 0 ? -rounded : rounded;
};

export const roundQty = (value: number): number => roundScaled(value, QTY_SCALE);

export const roundMoney = (value: number): number => roundScaled(value, MONEY_SCALE);

const toMoneyNumber = (value: unknown): number => {
	const n = Number(value);
	return Number.isFinite(n) ? n : 0;
};

/** Exact money sum: adds whole cents, so 0.1 + 0.2 is 0.3. */
export const sumMoney = (values: Iterable<unknown>): number => {
	let cents = 0;
	for (const value of Array.from(values)) {
		cents += Math.round(roundMoney(toMoneyNumber(value)) * moneyFactor);
	}
	return cents / moneyFactor;
};

/**
 * Line total as the database stores it: CAST(ABS(qty) * price AS DECIMAL(18,2)),
 * signed like qty * price. Computed in integer units so 0.3 x 1.15 is 0.35.
 */
export const lineTotal = (quantity: unknown, price: unknown): number => {
	const qty = toMoneyNumber(quantity);
	const amount = toMoneyNumber(price);
	const sign = Math.sign(qty) * Math.sign(amount);
	if (sign === 0) return 0;
	const qtyUnits = Math.round(roundQty(Math.abs(qty)) * qtyFactor);
	const priceCents = Math.round(roundMoney(Math.abs(amount)) * moneyFactor);
	const product = qtyUnits * priceCents;
	if (!Number.isSafeInteger(product)) return sign * roundMoney(Math.abs(qty * amount));
	return (sign * Math.floor((product + qtyFactor / 2) / qtyFactor)) / moneyFactor;
};

/** a + b at cent precision. */
export const addMoney = (a: unknown, b: unknown): number => sumMoney([a, b]);

/** -1, 0 or 1 comparing two money values at cent precision. */
export const compareMoney = (a: unknown, b: unknown): number =>
	Math.sign(
		Math.round(roundMoney(toMoneyNumber(a)) * moneyFactor)
		- Math.round(roundMoney(toMoneyNumber(b)) * moneyFactor),
	);

export const isWholeNumber = (value: number): boolean =>
	Number.isFinite(value) && Math.abs(value - Math.round(value)) < 1 / qtyFactor / 2;

export const parseQtyInput = (raw: string, fractional: boolean): number | null => {
	const parsed = fractional ? Number(raw) : parseInt(raw, 10);
	if (!Number.isFinite(parsed)) return null;
	const rounded = fractional ? roundQty(parsed) : parsed;
	if (!fractional && !Number.isInteger(rounded)) return null;
	return rounded;
};

export const parseMoneyInput = (raw: string): number | null => {
	const parsed = Number(raw);
	if (!Number.isFinite(parsed)) return null;
	return roundMoney(parsed);
};

/** Increment applied by stepper +/- buttons. */
export const qtyStep = (fractional: boolean): number => (fractional ? 0.01 : 1);

/**
 * Value for the HTML `step` attribute. Fractional quantities use "any": the
 * native step base is `min` (qtyMin -> 0.0001), so a fixed 0.01 step would make
 * ordinary values like 0.5 or 2.5 fail constraint validation and block submit.
 */
export const qtyStepAttr = (fractional: boolean): number | "any" =>
	fractional ? "any" : 1;

export const qtyMin = (fractional: boolean): number => (fractional ? 1 / qtyFactor : 1);
